<template>
  <div :style="styleVars" class="min-h-screen flex flex-col bg-white">
    <!-- Loading Animation -->
    <div v-if="isLoading" class="min-h-screen flex flex-col bg-white">
      <!-- Header Skeleton -->
      <header class="w-full shadow-lg border-b border-gray-100 py-4 px-6 flex items-center justify-between z-50 bg-white">
        <div class="flex items-center gap-4">
          <div class="relative">
            <div class="bg-gray-200 rounded-lg animate-pulse" style="height: 3rem; width: 3rem;"></div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="h-6 bg-gray-200 rounded animate-pulse" style="width: 150px;"></div>
            <div class="h-4 bg-gray-200 rounded animate-pulse" style="width: 100px;"></div>
          </div>
        </div>
        <div class="hidden md:flex">
          <div class="h-10 bg-gray-200 rounded-lg animate-pulse" style="width: 140px;"></div>
        </div>
      </header>

      <!-- Hero Section Skeleton -->
      <section class="relative py-20 px-6" :style="{ background: 'var(--hero-section-bg-color)' }">
        <div class="max-w-6xl mx-auto">
          <div class="flex flex-col items-center text-center max-w-6xl mx-auto">
            <div class="space-y-8 mb-12 w-full">
              <div class="space-y-4">
                <div class="h-16 bg-gray-200 rounded animate-pulse mx-auto" style="width: 300px;"></div>
                <div class="h-6 bg-gray-200 rounded animate-pulse mx-auto" style="width: 80%; max-width: 600px;"></div>
                <div class="h-6 bg-gray-200 rounded animate-pulse mx-auto" style="width: 60%; max-width: 500px;"></div>
              </div>
              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <div class="h-12 bg-gray-200 rounded-lg animate-pulse" style="width: 180px;"></div>
                <div class="h-12 bg-gray-200 rounded-lg animate-pulse" style="width: 120px;"></div>
              </div>
            </div>
            <div class="w-full max-w-6xl">
              <div class="h-96 bg-gray-200 rounded-2xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Reviews Section Skeleton -->
      <section class="py-16 px-6" :style="{ backgroundColor: 'var(--reviews-section-bg-color)' }">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <div class="h-10 bg-gray-200 rounded animate-pulse mx-auto mb-4" style="width: 250px;"></div>
            <div class="h-6 bg-gray-200 rounded animate-pulse mx-auto" style="width: 350px;"></div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div v-for="i in 3" :key="i" 
                 :style="{ 
                   backgroundColor: 'var(--review-card-bg-color)', 
                   borderRadius: 'var(--review-card-border-radius)',
                   border: 'var(--review-card-border-size) solid var(--store-border-color)'
                 }"
                 class="p-8 shadow-lg">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                <div class="flex-1">
                  <div class="h-4 bg-gray-200 rounded animate-pulse mb-2" style="width: 70%;"></div>
                  <div class="flex gap-1">
                    <div v-for="star in 5" :key="star" class="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded animate-pulse" style="width: 100%;"></div>
                <div class="h-4 bg-gray-200 rounded animate-pulse" style="width: 80%;"></div>
                <div class="h-4 bg-gray-200 rounded animate-pulse" style="width: 60%;"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section Skeleton -->
      <section class="py-12 px-6 bg-white">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-8">
            <div class="h-10 bg-gray-200 rounded animate-pulse mx-auto mb-4" style="width: 220px;"></div>
            <div class="h-6 bg-gray-200 rounded animate-pulse mx-auto" style="width: 400px;"></div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div class="bg-gray-50 rounded-xl p-6">
                <div class="h-6 bg-gray-200 rounded animate-pulse mb-4" style="width: 120px;"></div>
                <div class="space-y-4">
                  <div v-for="i in 4" :key="i" class="flex items-start gap-3">
                    <div class="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div class="flex-1">
                      <div class="h-4 bg-gray-200 rounded animate-pulse mb-2" style="width: 60px;"></div>
                      <div class="h-4 bg-gray-200 rounded animate-pulse" style="width: 80%;"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 rounded-xl p-4">
              <div class="h-6 bg-gray-200 rounded animate-pulse mb-3" style="width: 150px;"></div>
              <div class="h-80 bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer Skeleton -->
      <footer class="bg-gray-900 text-white py-12 px-6">
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div class="flex items-center gap-3 mb-4">
                <div class="h-10 w-10 bg-gray-700 rounded animate-pulse"></div>
                <div class="h-6 bg-gray-700 rounded animate-pulse" style="width: 120px;"></div>
              </div>
            </div>
            <div>
              <div class="h-6 bg-gray-700 rounded animate-pulse mb-4" style="width: 100px;"></div>
              <div class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-4 bg-gray-700 rounded animate-pulse" style="width: 80%;"></div>
              </div>
            </div>
            <div>
              <div class="h-6 bg-gray-700 rounded animate-pulse mb-4" style="width: 100px;"></div>
              <div class="space-y-2">
                <div v-for="i in 3" :key="i" class="h-4 bg-gray-700 rounded animate-pulse" style="width: 70%;"></div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>

    <!-- Actual Content (shown when not loading) -->
    <div v-else>
    <!-- Header -->
      <header class="w-full shadow-lg border-b border-gray-100 py-4 px-6 flex items-center justify-between z-50" :style="{ backgroundColor: 'var(--header-background-color)' }">
      <div class="flex items-center gap-4">
        <div class="relative">
          <img v-if="storeLogo" :src="storeLogo" :alt="brandName" class="object-contain rounded-lg shadow-sm" :style="{ height: 'var(--header-logo-size)', width: 'auto' }" @error="logoLoadError = true" />
          <div v-else class="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm flex items-center justify-center" :style="{ height: 'var(--header-logo-size)', width: 'var(--header-logo-size)' }">
            <v-icon color="white" :size="parseInt(headerStyles.logoSize || '48') / 2">mdi-store</v-icon>
          </div>
        </div>
        <div class="flex flex-col">
          <span class="font-bold" :style="{ fontSize: 'var(--header-title-size)', color: 'var(--header-text-color)' }">{{ brandName }}</span>
          <span class="text-sm" :style="{ color: 'var(--header-text-color)' }">{{ storeLocation?.city }}{{ storeLocation?.city && storeLocation?.state ? ', ' : '' }}{{ storeLocation?.state }}</span>
        </div>
      </div>
      
      <!-- Navigation -->
      <div class="hidden md:flex items-center gap-6">
        <!-- Dynamic Header Tabs -->
        <div v-if="availableTabs.length > 0" class="flex items-center gap-4">
          <button
            v-for="tab in availableTabs"
            :key="tab.value"
            @click="handleTabClick(tab)"
            class="text-base font-medium"
            :class="[
              activeTab === tab.value
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            ]"
          >
            {{ tab.label }}
          </button>
        </div>
        
        <!-- Appointment Button -->
        <!--
        <button v-if="appointments" @click="scrollToAppointment" 
                class="px-6 py-2 font-semibold appointment-button">
          Get Appointment
        </button>
        -->
        <!-- Call Now Button (if phone is available) -->
        <button v-if="getGmbPhone(storeLocation)" @click="callBusiness"
                class="px-6 py-2 font-semibold appointment-button">
          Call {{ getGmbPhone(storeLocation) }}
        </button>
      </div>
    </header>

    <!-- Hero Section -->
      <section v-if="showHeroSection" class="relative py-20 px-6" :style="{ background: 'var(--hero-section-bg-color)' }">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col items-center text-center max-w-6xl mx-auto">
          <!-- Brand Name -->
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight"
              :style="{ fontSize: 'var(--hero-brand-name-size)', color: 'var(--hero-brand-name-color)' }">
            {{ brandName }}
          </h1>

          <!-- Rating and Review Count (centered, single row) -->
          <div
            v-if="storeLocation?.averageRating || storeLocation?.reviewCount"
            class="flex items-center gap-2 mb-4 mt-2"
            style="justify-content: center;"
          >
            <!-- Star Rating -->
            <div class="flex items-center">
              <v-icon
                v-for="star in 5"
                :key="star"
                :color="star <= Math.round(storeLocation.averageRating) ? 'amber' : 'grey-lighten-2'"
                size="24"
              >
                {{ star <= Math.round(storeLocation.averageRating) ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </div>
            <!-- Numeric Rating -->
            <span v-if="storeLocation?.averageRating" class="text-lg font-semibold text-gray-700 ml-2">
              {{ storeLocation.averageRating.toFixed(1) }}
            </span>
            <!-- Dot Separator -->
            <span v-if="storeLocation?.averageRating && storeLocation?.reviewCount" class="text-lg text-gray-600 mx-1">•</span>
            <!-- Review Count -->
            <span v-if="storeLocation?.reviewCount" class="text-lg text-gray-600">
              {{ storeLocation.reviewCount }} {{ storeLocation.reviewCount === 1 ? 'review' : 'reviews' }}
            </span>
          </div>

          <!-- Description -->
          <p class="text-xl text-gray-600 leading-relaxed"
             :style="{ fontSize: 'var(--hero-description-size)', color: 'var(--hero-description-color)' }">
            {{ getBusinessDescription(storeLocation) || `Welcome to ${brandName}, where quality service meets excellence. Our expert team is committed to providing you with the best experience in a comfortable, welcoming environment.` }}
          </p>
        </div>
      </div>
    </section>

    <!-- Products & Services Section -->
    <!--
    <section v-if="showProductsSection && availableServices.length > 0" class="py-16 px-6 bg-white">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Products & Services</h2>
          <p class="text-lg text-gray-600">Explore our comprehensive range of products and services</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(service, index) in availableServices" :key="index" class="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div v-if="service.image" class="w-full h-48 mb-6 rounded-xl overflow-hidden">
              <img 
                :src="service.image" 
                :alt="service.name" 
                class="w-full h-full object-cover"
              />
            </div>
            <div v-else class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <v-icon color="blue" size="32">mdi-cog</v-icon>
            </div>
            <h3 class="text-xl font-bold text-gray-800 mb-4">{{ service.name }}</h3>
            <p v-if="service.description" class="text-gray-600 mb-4 leading-relaxed">{{ service.description }}</p>
            <div v-if="service.price" class="text-lg font-semibold text-blue-600">{{ service.price }}</div>
          </div>
        </div>
      </div>
    </section>
    -->

    <!-- Reviews Section -->
      <section id="reviews" v-if="showReviewsSection && reviews.length > 0" class="py-16 px-6" :style="{ backgroundColor: 'var(--reviews-section-bg-color)' }">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">What Our Customers Say</h2>
          <p class="text-lg text-gray-600">Read testimonials from satisfied customers</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div v-for="review in reviews.slice(0, 6)" :key="review.reviewId" 
                 :style="{ 
                   backgroundColor: 'var(--review-card-bg-color)', 
                   borderRadius: 'var(--review-card-border-radius)',
                   border: 'var(--review-card-border-size) solid var(--store-border-color)',
                   color: 'var(--review-card-text-color)'
                 }"
                 class="p-8 shadow-lg">
            <div class="flex items-center gap-3 mb-4">
              <img :src="review.profilePhotoUrl" alt="profile" class="w-12 h-12 rounded-full object-cover" @error="event => event.target.src = 'https://www.gravatar.com/avatar/?d=mp'" />
              <div>
                  <p class="font-semibold" :style="{ color: 'var(--review-card-text-color)' }">{{ review.displayName }}</p>
                <div class="flex">
                  <v-icon v-for="star in 5" :key="star" :color="star <= review.starRating ? 'amber' : 'grey-lighten-2'" size="16">
                    {{ star <= review.starRating ? 'mdi-star' : 'mdi-star-outline' }}
                  </v-icon>
                </div>
              </div>
            </div>
              <p class="italic" :style="{ color: 'var(--review-card-text-color)' }">"{{ review.comment }}"</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Appointment Section -->
    <!--
    <section v-if="showAppointmentSection && appointments" id="appointment" class="py-16 px-6 bg-gray-50">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Book Your Appointment</h2>
          <p class="text-lg text-gray-600">Take the first step towards better service! Our team is here to help you.</p>
        </div>

        <div class="bg-white p-8 shadow-lg max-w-2xl mx-auto" :style="{ borderRadius: 'var(--appointment-form-border-radius)' }">
          <h3 class="text-2xl font-bold text-gray-800 mb-6">Schedule Your Visit</h3>
          <form @submit.prevent="submitAppointment" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                <input v-model="appointmentForm.firstName" type="text" required class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                <input v-model="appointmentForm.lastName" type="text" required class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input v-model="appointmentForm.email" type="email" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
              <input v-model="appointmentForm.phone" type="tel" required class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Preferred Date *</label>
              <input v-model="appointmentForm.date" type="date" required :min="new Date().toISOString().split('T')[0]" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
              <select v-model="appointmentForm.time" required class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">Select a time</option>
                <option value="09:00">9:00 AM</option>
                <option value="10:00">10:00 AM</option>
                <option value="11:00">11:00 AM</option>
                <option value="12:00">12:00 PM</option>
                <option value="13:00">1:00 PM</option>
                <option value="14:00">2:00 PM</option>
                <option value="15:00">3:00 PM</option>
                <option value="16:00">4:00 PM</option>
                <option value="17:00">5:00 PM</option>
              </select>
            </div>
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Message</label>
              <textarea v-model="appointmentForm.message" rows="4" placeholder="Tell us about your needs..." class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
            </div>
            
            <button type="submit" :disabled="isSubmitting" 
                    class="w-full py-4 font-bold disabled:opacity-50 disabled:cursor-not-allowed appointment-button">
              <span v-if="isSubmitting">Submitting...</span>
              <span v-else>Book Appointment</span>
            </button>
          </form>
        </div>
      </div>
    </section>
    -->

    <!-- Contact Us Section -->
    <section id="contact" v-if="showContactSection" class="contact-section py-12 px-6" :style="{ backgroundColor: 'var(--contact-section-bg-color)' }">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-8">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Contact Information</h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">Get in touch with us. We're here to help you with all your needs.</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Left Column: Contact Details & Business Hours -->
          <div class="space-y-4">
            <!-- Contact Details -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-xl font-bold text-gray-800 mb-4">Get In Touch</h3>
              
              <div class="space-y-4">
                <!-- Phone -->
                <div v-if="getGmbPhone(storeLocation)" class="flex items-start gap-3">
                  <div>
                    <h4 class="text-base font-semibold text-gray-800 mb-1">Phone</h4>
                    <a :href="`tel:${getGmbPhone(storeLocation)}`" class="text-base text-blue-600 font-medium hover:text-blue-700">
                      {{ getGmbPhone(storeLocation) }}
                    </a>
                  </div>
                </div>

                <!-- Email -->
                <div v-if="storeLocation?.email" class="flex items-start gap-3">
                  <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <v-icon color="white" size="16">mdi-email</v-icon>
                  </div>
                  <div>
                    <h4 class="text-base font-semibold text-gray-800 mb-1">Email</h4>
                    <a :href="`mailto:${storeLocation.email}`" class="text-base text-green-600 font-medium hover:text-green-700 break-all">
                      {{ storeLocation.email }}
                    </a>
                  </div>
                </div>

                <!-- Address -->
                <div class="flex items-start gap-3">
                  <div>
                    <h4 class="text-base font-semibold text-gray-800 mb-1">Address</h4>
                    <div class="text-base text-gray-700">
                      <div class="font-medium mb-1">{{ storeLocation?.name || storeLocation?.storeLocation || 'Our Location' }}</div>
                      <div>{{ getFullAddress(storeLocation) }}</div>
                    </div>
                  </div>
                </div>



                <!-- Website -->
                <div v-if="storeLocation?.website" class="flex items-start gap-3">
                  <div class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <v-icon color="white" size="16">mdi-web</v-icon>
                  </div>
                  <div>
                    <h4 class="text-base font-semibold text-gray-800 mb-1">Website</h4>
                    <a :href="storeLocation.website" target="_blank" class="text-base text-indigo-600 font-medium hover:text-indigo-700">
                      {{ formatWebsite(storeLocation.website) }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Business Hours -->
            <div class="bg-gray-50 rounded-xl p-4">
              <h3 class="text-lg font-bold text-gray-800 mb-3">Business Hours</h3>
              <div class="space-y-1">
                <div v-for="hours in getOpeningHours(storeLocation)" :key="hours.day" 
                     class="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0">
                  <span class="text-sm font-medium text-gray-700">{{ hours.day }}</span>
                  <span class="text-sm text-gray-600">{{ hours.hours }}</span>
                </div>
                <div v-if="!getOpeningHours(storeLocation).length" class="text-gray-500 text-center py-2 text-sm">
                  Business hours not available
                </div>
              </div>
            </div>

            <!-- Social Media -->
            <div v-if="getSocialLinks(storeLocation).length > 0" class="bg-gray-50 rounded-xl p-4">
              <h3 class="text-lg font-bold text-gray-800 mb-3">Find us on social media</h3>
              <div class="flex flex-wrap gap-2">
                <a v-for="social in getSocialLinks(storeLocation)" :key="social.platform"
                   :href="social.url" target="_blank"
                   class="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 hover:shadow-md">
                  <v-icon :color="social.iconColor" size="16">{{ social.icon }}</v-icon>
                  <span class="text-xs font-medium text-gray-700">{{ social.platform }}</span>
                </a>
              </div>
            </div>
          </div>

          <!-- Right Column: Map -->
          <div class="bg-gray-50 rounded-xl p-4 h-full">
            <h3 class="text-lg font-bold text-gray-800 mb-3">Find us on Google Maps</h3>
            <div class="flex-1 rounded-lg overflow-hidden shadow-lg border border-gray-200" style="height: calc(100% - 60px);">
              <iframe v-if="storeLocation && ((apifyLat && apifyLng) || storeLocation.place_id || getFullAddress(storeLocation))" 
                      :src="getGoogleMapsSrc()" 
                      width="100%" 
                      height="100%" 
                      style="border:0;" 
                      allowfullscreen 
                      loading="lazy" 
                      referrerpolicy="no-referrer-when-downgrade">
              </iframe>
              <div v-else class="flex items-center justify-center h-full bg-gray-100 text-gray-500">
                <div class="text-center">
                  <v-icon size="24" color="gray" class="mb-2">mdi-map-outline</v-icon>
                  <p class="text-sm font-semibold">Map not available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Gallery Section -->
    <section id="posts" v-if="showPostsSection && galleryPosts.length > 0" class="py-16 px-6 bg-white">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Latest Posts</h2>
          <p class="text-lg text-gray-600">Check out our recent updates and content</p>
          </div>

        <!-- Loading State -->
        <div v-if="isGalleryLoading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
          <p class="text-gray-500 mt-2">Loading posts...</p>
        </div>

        <!-- Posts Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="post in galleryPosts" :key="post.id" class="bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-100">
            <!-- Social Media Icons and Date -->
            <div class="flex items-center gap-2 mb-4">
              <div class="flex gap-1">
                <v-avatar
                  tile
                  size="20" 
                  v-for="(socialMedia, index) in post.social_media"
                  :key="index"
                >
                  <v-img
                    :src="socialMedia.logo_url"
                    :alt="socialMedia.name"
                  ></v-img>
                </v-avatar>
              </div>
              <span class="text-xs text-gray-500">
                {{ formatDate(post.created_at?.seconds ? post.created_at.seconds * 1000 : post.created_at) }}
              </span>
            </div>
            
            <!-- Images on Top - Larger Size -->
            <div v-if="post.images && post.images.length > 0" class="mb-4">
              <div class="grid grid-cols-1 gap-3">
                <img
                  v-for="(image, index) in post.images.slice(0, 2)"
                  :key="index"
                  :src="image"
                  alt="post image"
                  class="w-full h-auto max-h-96 object-contain bg-gray-100 rounded-lg shadow-md"
                />
                <div v-if="post.images.length > 2" class="grid grid-cols-3 gap-2">
                  <img
                    v-for="(image, index) in post.images.slice(2, 5)"
                    :key="index"
                    :src="image"
                    alt="post image"
                    class="w-full h-auto max-h-48 object-contain bg-gray-100 rounded-lg shadow-sm"
                  />
                  <div v-if="post.images.length > 5" class="h-24 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                    +{{ post.images.length - 5 }}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Videos on Top - Larger Size -->
            <div v-if="post.videos && post.videos.length > 0" class="mb-4">
              <div class="grid grid-cols-1 gap-3">
                <div
                  v-for="(video, index) in post.videos.slice(0, 2)"
                  :key="index"
                  class="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
                >
                  <v-icon size="48" color="white" class="absolute">mdi-play-circle</v-icon>
                  <div class="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    Video
                  </div>
                </div>
                <div v-if="post.videos.length > 2" class="grid grid-cols-3 gap-2">
                  <div
                    v-for="(video, index) in post.videos.slice(2, 5)"
                    :key="index"
                    class="h-24 bg-gray-200 rounded-lg flex items-center justify-center relative"
                  >
                    <v-icon size="24" color="white">mdi-play</v-icon>
                  </div>
                  <div v-if="post.videos.length > 5" class="h-24 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                    +{{ post.videos.length - 5 }}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Post Title -->
            <div v-if="post.name" class="text-lg font-semibold text-gray-800 mb-2">
              {{ post.name }}
            </div>
            
            <!-- Post Text -->
            <div class="text-gray-700 text-base leading-relaxed">
              <!-- Debug info -->
              <div class="text-xs text-gray-400 mb-1">
                Post ID: {{ post.id }} | Expanded: {{ expandedPosts[post.id] }}
              </div>
              <span v-if="post.text">
                {{ (post.text.length > 200 && !expandedPosts[post.id]) ? post.text.substring(0, 200) + '...' : post.text }}
              </span>
              <span 
                v-if="post.text && post.text.length > 200" 
                @click="toggleReadMore(post.id)" 
                class="text-blue-500 hover:underline ml-1 font-semibold text-sm cursor-pointer"
              >
                {{ expandedPosts[post.id] ? 'Show less' : 'Show more' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Social Media Links Section -->
    <section v-if="showSocialMediaSection && socialMediaLinks.length > 0" class="py-16 px-6 bg-gray-50">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Find us on social media</h2>
          <p class="text-lg text-gray-600">Connect with us and stay updated with our latest news and updates</p>
        </div>
        <div class="flex justify-center items-center gap-6">
          <a 
            v-for="social in socialMediaLinks" 
            :key="social.platform"
            :href="social.url" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <v-icon :color="social.iconColor" size="48">{{ social.icon }}</v-icon>
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer v-if="showFooter" class="bg-white border-t border-gray-200 py-8 px-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
      <div class="max-w-4xl mx-auto text-center">
        <div class="flex items-center justify-center gap-3 mb-4">
          <img v-if="storeLogo" :src="storeLogo" :alt="brandName" class="h-8 w-auto object-contain" />
          <div v-else class="h-8 w-8 bg-blue-500 rounded flex items-center justify-center">
            <v-icon color="white" size="16">mdi-store</v-icon>
          </div>
          <h3 class="text-lg font-semibold text-gray-800">{{ brandName }}</h3>
        </div>
        
        <div class="text-sm text-gray-600 mb-4">
          <p v-if="getGmbPhone(storeLocation)" class="mb-1">{{ getGmbPhone(storeLocation) }}</p>
          <p v-if="storeLocation?.email" class="mb-1">{{ storeLocation.email }}</p>
          <p>{{ getFullAddress(storeLocation) }}</p>
        </div>
        
        <div class="border-t border-gray-200 pt-4 text-xs text-gray-500">
          <p>&copy; {{ new Date().getFullYear() }} {{ brandName }}. All rights reserved.</p>
        </div>
      </div>
    </footer>
    </div>
  </div>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import { doc, getDoc, collection, getDocs, orderBy, limit } from "firebase/firestore";
import { auth } from "@/utils/firebase.utils";
import { query, where } from "firebase/firestore";
import { functions } from "@/utils/firebase.utils";
import { httpsCallable } from "firebase/functions";

export default {
  props: {
    userUid: {
      type: String,
      default: null
    },
    wid: {
      type: String,
      default: null
    },
    locationId: {
      type: String,
      default: null
    },
    slug: {
      type: String,
      default: null
    },
    tabvalue: {
      type: String,
      default: null
    },
    disableTitleAndFavicon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: true,
      storeLocation: null,
      workspaceData: null,
      gmbSettingsData: null, // Add this to store GMB settings data
      apifyLat: null,
      apifyLng: null,
      logoLoadError: false,
      coverPhotoError: false,
      reviews: [],
      isReviewsLoading: false,
      reviewsPage: 1,
      reviewsPerPage: 5,
      totalReviews: 0,
      _originalFaviconHref: null,
      _originalPageTitle: null,
      appointmentForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        service: '',
        message: ''
      },
      isSubmitting: false,
      galleryPosts: [],
      isGalleryLoading: false,
      expandedPosts: {},
      activeTab: null
    };
  },
  computed: {
    brandName() {
      // Use location title from database, following StoreLocator logic
      if (this.storeLocation) {
        return this.storeLocation?.gmb_shallow_profile_data
          ? this.storeLocation?.gmb_shallow_profile_data?.title
          : this.storeLocation?.gmbProfile?.title || this.storeLocation.name || this.storeLocation.storeLocation || "Store Brand";
      }
      return this.workspaceData?.name || "Store Brand";
    },
    storeLogo() {
      if (!this.workspaceData) return null;
      return this.workspaceData.logo_original || this.workspaceData.logo_url || this.workspaceData.logo_dark || this.workspaceData.logo_transparent || this.workspaceData.logo;
    },
    mergedIndividualStoreStyles() {
      // Merge workspace data with any prop styles, workspace data takes precedence
      const workspaceStyles = this.workspaceData?.location_common_styles || {};
      return { ...workspaceStyles };
    },
    styleVars() {
      // Get location-specific styles from location_style map (individual store preferences)
      const locationStyles = this.storeLocation?.location_style || {};
      // Get workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles || {};
      // Get header styles from common styles
      const headerStyles = commonStyles?.header_styles || {};
      
      // Helper function to get value with individual store preference
      const getStyleValue = (individualKey, commonKey, defaultValue) => {
        // First check individual store styles
        if (locationStyles[individualKey] !== undefined) {
          return locationStyles[individualKey];
        }
        // Then check common styles
        if (commonStyles[commonKey] !== undefined) {
          return commonStyles[commonKey];
        }
        // Finally use default
        return defaultValue;
      };
      
      return {
        // Location styles - individual store takes precedence
        '--store-primary': getStyleValue('primaryColor', 'primaryColor', '#1773EA'),
        '--store-secondary': getStyleValue('secondaryColor', 'secondaryColor', '#1d4ed8'),
        '--store-accent': getStyleValue('accentColor', 'accentColor', '#3b82f6'),
        '--store-background': getStyleValue('backgroundColor', 'backgroundColor', '#ffffff'),
        '--store-text-primary': getStyleValue('textPrimaryColor', 'textPrimaryColor', '#1f2937'),
        '--store-text-secondary': getStyleValue('textSecondaryColor', 'textSecondaryColor', '#6b7280'),
        '--store-font-family': getStyleValue('fontFamily', 'fontFamily', 'Inter, sans-serif'),
        '--store-brand-name-size': getStyleValue('brandNameSize', 'brandNameSize', '2rem'),
        '--store-brand-name-color': getStyleValue('brandNameColor', 'brandNameColor', '#0a2540'),
        '--store-button-primary': getStyleValue('buttonPrimaryColor', 'buttonPrimaryColor', '#3b82f6'),
        '--store-button-secondary': getStyleValue('buttonSecondaryColor', 'buttonSecondaryColor', '#ffffff'),
        '--store-button-text': getStyleValue('buttonTextColor', 'buttonTextColor', '#ffffff'),
        '--store-border-color': getStyleValue('borderColor', 'borderColor', '#e5e7eb'),
        '--store-shadow': getStyleValue('shadowColor', 'shadowColor', 'rgba(0, 0, 0, 0.1)'),
        '--store-gradient-start': getStyleValue('gradientStart', 'gradientStart', '#3b82f6'),
        '--store-gradient-end': getStyleValue('gradientEnd', 'gradientEnd', '#1d4ed8'),
        
        // Header styles - from common styles only (no individual store override)
        '--header-logo-size': headerStyles.logoSize || '3rem',
        '--header-title-size': headerStyles.titleTextSize || '1.25rem',
        '--header-text-color': headerStyles.textColor || '#1f2937',
        '--header-background-color': headerStyles.backgroundColor || '#ffffff',
        '--store-tab-text': headerStyles.tabTextColor || '#333',
        '--store-tab-hover-text': headerStyles.tabHoverTextColor || '#1773EA',
        '--store-tab-selected-text': headerStyles.tabSelectedTextColor || '#fff',
        '--store-tab-bg': headerStyles.tabBg || '#f3f4f6',
        '--store-tab-hover-bg': headerStyles.tabHoverBg || '#e0e7ff',
        '--store-tab-selected-bg': headerStyles.tabSelectedBg || '#1773EA',
        '--store-tab-border': headerStyles.tabBorderColor || 'transparent',
        '--store-tab-hover-border': headerStyles.tabHoverBorderColor || '#1773EA',
        '--store-tab-selected-border': headerStyles.tabSelectedBorderColor || '#1773EA',
        '--store-tab-border-size': headerStyles.tabBorderSize || '1px',
        '--store-tab-hover-border-size': headerStyles.tabHoverBorderSize || '2px',
        '--store-tab-selected-border-size': headerStyles.tabSelectedBorderSize || '2px',
        '--store-tab-border-radius': headerStyles.tabBorderRadius || '0.5rem',
        
        // Hero Section Styles - from common styles only
        '--hero-brand-name-size': commonStyles?.hero_section?.brandNameSize || '4rem',
        '--hero-brand-name-color': commonStyles?.hero_section?.brandNameColor || '#1f2937',
        '--hero-description-size': commonStyles?.hero_section?.descriptionSize || '1.25rem',
        '--hero-description-color': commonStyles?.hero_section?.descriptionColor || '#6b7280',
        '--hero-cover-photo-height': commonStyles?.hero_section?.coverPhotoHeight || '24rem',
        '--hero-cover-photo-border-radius': commonStyles?.hero_section?.coverPhotoBorderRadius || '1rem',
        '--hero-section-bg-color': commonStyles?.hero_section?.backgroundColor || 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
        
        // Reviews Section Styles - from common styles only
        '--reviews-section-bg-color': commonStyles?.reviews_section?.backgroundColor || '#f9fafb',
        '--review-card-bg-color': commonStyles?.reviews_section?.cardBackgroundColor || '#ffffff',
        '--review-card-border-radius': commonStyles?.reviews_section?.cardBorderRadius || '1rem',
        '--review-card-border-size': commonStyles?.reviews_section?.cardBorderSize || '0px',
        '--review-card-text-color': commonStyles?.reviews_section?.cardTextColor || '#1f2937',
        
        // Contact Information Styles - from common styles only
        '--contact-section-bg-color': commonStyles?.contact_information?.backgroundColor || '#ffffff',
        
        // Appointment Styles - from common styles only
        '--appointment-button-bg-color': commonStyles?.appointment_styles?.buttonBackgroundColor || 'linear-gradient(to right, #3b82f6, #1d4ed8)',
        '--appointment-button-text-color': commonStyles?.appointment_styles?.buttonTextColor || '#ffffff',
        '--appointment-button-border-radius': commonStyles?.appointment_styles?.buttonBorderRadius || '0.5rem',
        '--appointment-button-border-size': commonStyles?.appointment_styles?.buttonBorderSize || '0px',
        '--appointment-button-border-color': commonStyles?.appointment_styles?.buttonBorderColor || 'transparent',
        '--appointment-button-hover-bg-color': commonStyles?.appointment_styles?.buttonHoverBackgroundColor || 'linear-gradient(to right, #2563eb, #1e40af)',
        '--appointment-button-hover-text-color': commonStyles?.appointment_styles?.buttonHoverTextColor || '#ffffff',
        '--appointment-form-border-radius': commonStyles?.appointment_styles?.formBorderRadius || '1rem',
      };
    },
    appointments() {
      // Check individual location settings first (highest priority)
      if (this.storeLocation?.location_style?.appointments !== undefined) {
        return this.storeLocation.location_style.appointments === true;
      }
      
      // Check workspace-level settings (fallback)
      const commonStyles = this.workspaceData?.location_common_styles || {};
      if (commonStyles.appointments !== undefined) {
        return commonStyles.appointments === true;
      }
      
      // Default to false if not specified anywhere
      return false;
    },
    headerStyles() {
      return this.mergedIndividualStoreStyles?.header_styles || {};
    },
    availableTabs() {
      const tabs = [];
      
      // Always show Home tab
      tabs.push({
        label: 'Home',
        value: 'home'
      });
      
      // Show Reviews tab if reviews are available
      if (this.reviews && this.reviews.length > 0) {
        tabs.push({
          label: 'Reviews',
          value: 'reviews'
        });
      }
      
      // Show Posts tab if gallery posts are available
      if (this.galleryPosts && this.galleryPosts.length > 0) {
        tabs.push({
          label: 'Posts',
          value: 'posts'
        });
      }
      
      // Show Contact tab if contact information is available
      if (this.storeLocation && (
        this.getGmbPhone(this.storeLocation) || 
        this.storeLocation.email || 
        this.storeLocation.address ||
        this.getFullAddress(this.storeLocation)
      )) {
        tabs.push({
          label: 'Contact',
          value: 'contact'
        });
      }
      
      console.log('Available tabs:', tabs);
      console.log('Reviews count:', this.reviews?.length);
      console.log('Gallery posts count:', this.galleryPosts?.length);
      console.log('Store location:', this.storeLocation);
      
      return tabs;
    },
    showHeroSection() {
      return !this.tabvalue || this.tabvalue === 'home';
    },
    showProductsSection() {
      return !this.tabvalue || this.tabvalue === 'home';
    },
    showReviewsSection() {
      return this.tabvalue === 'reviews' || (!this.tabvalue && this.reviews && this.reviews.length > 0);
    },
    showPostsSection() {
      return this.tabvalue === 'posts' || (!this.tabvalue && this.galleryPosts && this.galleryPosts.length > 0);
    },
    showContactSection() {
      return this.tabvalue === 'contact' || !this.tabvalue || this.tabvalue === 'home';
    },
    showAppointmentSection() {
      return !this.tabvalue || this.tabvalue === 'home';
    },
    showSocialMediaSection() {
      return !this.tabvalue || this.tabvalue === 'home';
    },
    showFooter() {
      return true; // Show footer on all tabs
    },
    fullStars() {
      if (!this.storeLocation || !this.storeLocation.averageRating) return 0;
      return Math.floor(this.storeLocation.averageRating);
    },
    decimalPart() {
      if (!this.storeLocation || !this.storeLocation.averageRating) return 0;
      return this.storeLocation.averageRating - this.fullStars;
    },
    paginatedReviews() {
      const startIndex = (this.reviewsPage - 1) * this.reviewsPerPage;
      const endIndex = startIndex + this.reviewsPerPage;
      return this.reviews.slice(startIndex, endIndex);
    },
    totalReviewPages() {
      return Math.ceil(this.reviews.length / this.reviewsPerPage);
    },
    hasNextReviewPage() {
      return this.reviewsPage < this.totalReviewPages;
    },
    hasPrevReviewPage() {
      return this.reviewsPage > 1;
    },
    availableServices() {
      console.log('[availableServices] Starting to check for services...');
      console.log('[availableServices] storeLocation:', this.storeLocation);
      console.log('[availableServices] workspaceData:', this.workspaceData);
      console.log('[availableServices] gmbSettingsData:', this.gmbSettingsData);
      
      // Get services from GMB settings data (highest priority - this is where BusinessDetails.vue saves data)
      if (this.gmbSettingsData?.productsAndServices && Array.isArray(this.gmbSettingsData.productsAndServices)) {
        console.log('[availableServices] Found services in gmbSettingsData.productsAndServices:', this.gmbSettingsData.productsAndServices);
        const filtered = this.gmbSettingsData.productsAndServices.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from GMB settings:', filtered);
        return filtered;
      }
      
      // Get services from store location data
      if (!this.storeLocation) {
        console.log('[availableServices] No storeLocation found, returning empty array');
        return [];
      }
      
      // First priority: individual store configuration product_and_services
      if (this.storeLocation.product_and_services && Array.isArray(this.storeLocation.product_and_services)) {
        console.log('[availableServices] Found services in storeLocation.product_and_services:', this.storeLocation.product_and_services);
        const filtered = this.storeLocation.product_and_services.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from individual store config:', filtered);
        return filtered;
      }
      
      // Second priority: productsAndServices in the location data
      if (this.storeLocation.productsAndServices && Array.isArray(this.storeLocation.productsAndServices)) {
        console.log('[availableServices] Found services in storeLocation.productsAndServices:', this.storeLocation.productsAndServices);
        const filtered = this.storeLocation.productsAndServices.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from location data:', filtered);
        return filtered;
      }
      
      // Check for services in GMB profile data
      if (this.storeLocation.gmbProfile?.productsAndServices && Array.isArray(this.storeLocation.gmbProfile.productsAndServices)) {
        console.log('[availableServices] Found services in gmbProfile.productsAndServices:', this.storeLocation.gmbProfile.productsAndServices);
        const filtered = this.storeLocation.gmbProfile.productsAndServices.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from GMB profile:', filtered);
        return filtered;
      }
      
      // Check for services in shallow profile data
      if (this.storeLocation.gmb_shallow_profile_data?.productsAndServices && Array.isArray(this.storeLocation.gmb_shallow_profile_data.productsAndServices)) {
        console.log('[availableServices] Found services in gmb_shallow_profile_data.productsAndServices:', this.storeLocation.gmb_shallow_profile_data.productsAndServices);
        const filtered = this.storeLocation.gmb_shallow_profile_data.productsAndServices.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from shallow profile:', filtered);
        return filtered;
      }
      
      // Check for services in workspace data (lowest priority)
      if (this.workspaceData?.gmb_details?.productsAndServices && Array.isArray(this.workspaceData.gmb_details.productsAndServices)) {
        console.log('[availableServices] Found services in workspaceData.gmb_details.productsAndServices:', this.workspaceData.gmb_details.productsAndServices);
        const filtered = this.workspaceData.gmb_details.productsAndServices.filter(service => 
          service && service.name && service.name.trim() !== ''
        );
        console.log('[availableServices] Filtered services from workspace data:', filtered);
        return filtered;
      }
      
      console.log('[availableServices] No services found in any location, returning empty array');
      return [];
    },
    socialMediaLinks() {
      const links = [];
      
      // First priority: individual store configuration social_links
      if (this.storeLocation?.social_links) {
        Object.entries(this.storeLocation.social_links).forEach(([platform, url]) => {
          if (url && url.trim() !== '') {
            const platformConfig = this.getSocialPlatformConfig(platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Second priority: locationid.gmbProfile.socialInfo.links (only if no links found from first source)
      if (links.length === 0 && this.storeLocation?.gmbProfile?.socialInfo?.links && Array.isArray(this.storeLocation.gmbProfile.socialInfo.links)) {
        this.storeLocation.gmbProfile.socialInfo.links.forEach(link => {
          if (link.url && link.platform) {
            const platformConfig = this.getSocialPlatformConfig(link.platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: link.url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Third priority: workspace social_links (only if no links found from first two sources)
      if (links.length === 0 && this.workspaceData?.social_links) {
        Object.entries(this.workspaceData.social_links).forEach(([platform, url]) => {
          if (url && url.trim() !== '') {
            const platformConfig = this.getSocialPlatformConfig(platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      return links;
    },
  },
  watch: {
    workspaceData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          console.log("Workspace data updated, updating favicon and title...");
          this.updateFavicon();
          this.updatePageTitle();
        }
      }
    },
    brandName: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== 'Store Brand') {
          console.log("Brand name updated, updating page title...", newVal);
          this.updatePageTitle();
        }
      }
    },
    availableServices: {
      immediate: true,
      handler(newVal) {
        console.log('[availableServices] Watcher triggered, services count:', newVal.length);
        console.log('[availableServices] Services data:', newVal);
      }
    },
    tabvalue: {
      immediate: true,
      handler(newTabValue) {
        if (newTabValue) {
          this.activeTab = newTabValue;
        } else {
          // Default to home if no tab value in URL
          this.activeTab = 'home';
        }
      }
    },
  },
  methods: {
    generateSlug(text) {
      if (!text) return '';
      return text
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    
    updateURLWithSlug() {
      if (this.storeLocation && (this.storeLocation.name || this.storeLocation.storeLocation)) {
        const storeName = this.storeLocation.name || this.storeLocation.storeLocation;
        const generatedSlug = this.generateSlug(storeName);
        
        // Only update if the current slug doesn't match the generated one
        if (this.slug !== generatedSlug) {
          const newRoute = {
            name: 'individual-store',
            params: {
              location: this.locationParam,
              uid: this.userUid,
              wid: this.wid,
              locationId: this.locationId,
              slug: generatedSlug
            }
          };
          
          // Use replace to avoid adding to browser history
          this.$router.replace(newRoute);
        }
      }
    },
    
    async fetchStoreData() {
      this.isLoading = true;
      
      // Validate props before making Firestore calls
      if (!this.userUid || !this.wid || !this.locationId) {
        console.error("Missing required props:", { userUid: this.userUid, wid: this.wid, locationId: this.locationId });
        this.isLoading = false;
        return;
      }
      
      try {
        // Fetch workspace data from /users/uid/fly_workspaces/wid
        const workspaceRef = doc(db, "users", this.userUid, "fly_workspaces", this.wid);
        const workspaceDoc = await getDoc(workspaceRef);
        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        }
        
        // Fetch GMB settings data
        await this.fetchGMBSettingsData();
        
        // Fetch location data from /users/uid/fly_workspaces/location-id
        const locationRef = doc(db, "users", this.userUid, "fly_workspaces", this.locationId);
        const locationDoc = await getDoc(locationRef);
        if (locationDoc.exists()) {
          this.storeLocation = locationDoc.data();
          
          // Update URL with correct slug after store data is loaded
          this.$nextTick(() => {
            this.updateURLWithSlug();
          });
          
          // Fetch reviews for this location
          this.fetchReviewsForLocation(this.storeLocation);
          
          // Fetch gallery posts for this location
          this.fetchGalleryPosts();
        }
        
        // Optionally fetch Apify lat/lng if needed
        if (this.storeLocation) {
          try {
            const apifyColRef = collection(db, "users", this.userUid, "fly_workspaces", this.locationId, "gmb_locations_apify_profiles");
            const apifyColSnap = await getDocs(apifyColRef);
            for (const docSnap of apifyColSnap.docs) {
              const data = docSnap.data();
              if (data && data.location && typeof data.location.lat === 'number' && typeof data.location.lng === 'number') {
                this.apifyLat = data.location.lat;
                this.apifyLng = data.location.lng;
                break;
              }
            }
          } catch (apifyError) {
            console.warn("Could not fetch Apify data:", apifyError);
          }
        }
      } catch (e) {
        console.error("Error fetching store data:", e);
        this.storeLocation = null;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGMBSettingsData() {
      try {
        console.log('[fetchGMBSettingsData] Starting to fetch GMB settings...');
        
        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.userUid,
          "fly_workspaces",
          this.wid,
          "gmb-settings"
        );
        const latestDocQuery = query(
          gmbSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const gmbData = latestDoc.data().gmb_details;
          console.log('[fetchGMBSettingsData] GMB data found:', gmbData);
          
          this.gmbSettingsData = gmbData;
          
          if (gmbData.productsAndServices) {
            console.log('[fetchGMBSettingsData] Products and services found:', gmbData.productsAndServices);
          } else {
            console.log('[fetchGMBSettingsData] No products and services found in GMB data');
          }
        } else {
          console.log('[fetchGMBSettingsData] No GMB settings documents found');
          this.gmbSettingsData = null;
        }
      } catch (error) {
        console.error('[fetchGMBSettingsData] Error fetching GMB settings:', error);
        this.gmbSettingsData = null;
      }
    },
    getFullAddress(location) {
      if (!location) return '';
      if (location.address) return location.address;
      if (location.gmbProfile?.storefrontAddress?.addressLines?.length) {
        return location.gmbProfile.storefrontAddress.addressLines.join(', ');
      }
      if (location.gmbProfile?.address) return location.gmbProfile.address;
      const parts = [location.name || location.storeLocation, location.sub_locality, location.city, location.state, location.zipcode, location.country].filter(Boolean);
      return parts.join(', ');
    },
    getBusinessDescription(location) {
      return location?.gmbProfile?.profile?.description || location?.gmb_shallow_profile_data?.description || null;
    },
    getCoverPhoto(location) {
      if (location?.gmb_shallow_profile_data?.cover_photo) return location.gmb_shallow_profile_data.cover_photo;
      if (location?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl) return location.gmbProfile.mediaItems.coverPhoto.googleUrl;
      return null;
    },
    getSocialLinks(location) {
      const socialLinks = [];
      if (location?.gmb_attributes && Array.isArray(location.gmb_attributes)) {
        const socialPlatforms = {
          'attributes/url_facebook': { platform: 'Facebook', icon: 'mdi-facebook', iconColor: '#1877F2' },
          'attributes/url_instagram': { platform: 'Instagram', icon: 'mdi-instagram', iconColor: '#E4405F' },
          'attributes/url_twitter': { platform: 'Twitter', icon: 'mdi-twitter', iconColor: '#1DA1F2' },
          'attributes/url_linkedin': { platform: 'LinkedIn', icon: 'mdi-linkedin', iconColor: '#0A66C2' },
          'attributes/url_pinterest': { platform: 'Pinterest', icon: 'mdi-pinterest', iconColor: '#BD081C' },
          'attributes/url_youtube': { platform: 'YouTube', icon: 'mdi-youtube', iconColor: '#FF0000' }
        };
        location.gmb_attributes.forEach(attr => {
          if (socialPlatforms[attr.name] && attr.value) {
            socialLinks.push({
              platform: socialPlatforms[attr.name].platform,
              url: attr.value,
              icon: socialPlatforms[attr.name].icon,
              iconColor: socialPlatforms[attr.name].iconColor
            });
          }
        });
      }
      if (location?.gmb_shallow_profile_data?.social_links && Array.isArray(location.gmb_shallow_profile_data.social_links)) {
        location.gmb_shallow_profile_data.social_links.forEach(link => {
          if (link.url && link.platform) {
            const platformConfig = {
              'facebook': { icon: 'mdi-facebook', iconColor: '#1877F2' },
              'instagram': { icon: 'mdi-instagram', iconColor: '#E4405F' },
              'twitter': { icon: 'mdi-twitter', iconColor: '#1DA1F2' },
              'linkedin': { icon: 'mdi-linkedin', iconColor: '#0A66C2' },
              'pinterest': { icon: 'mdi-pinterest', iconColor: '#BD081C' },
              'youtube': { icon: 'mdi-youtube', iconColor: '#FF0000' }
            };
            const config = platformConfig[link.platform.toLowerCase()];
            if (config) {
              socialLinks.push({
                platform: link.platform.charAt(0).toUpperCase() + link.platform.slice(1),
                url: link.url,
                icon: config.icon,
                iconColor: config.iconColor
              });
            }
          }
        });
      }
      return socialLinks;
    },
    getOpeningHours(location) {
      if (location?.gmb_shallow_profile_data?.opening_hours) {
        return location.gmb_shallow_profile_data.opening_hours.map(hours => ({
          day: this.formatDayName(hours.day),
          hours: hours.hours
        }));
      }
      if (location?.gmbProfile?.regularHours?.periods) {
        const daysMap = {
          'MONDAY': 'Monday',
          'TUESDAY': 'Tuesday',
          'WEDNESDAY': 'Wednesday',
          'THURSDAY': 'Thursday',
          'FRIDAY': 'Friday',
          'SATURDAY': 'Saturday',
          'SUNDAY': 'Sunday'
        };
        return location.gmbProfile.regularHours.periods.map(period => ({
          day: daysMap[period.openDay] || period.openDay,
          hours: period.openTime && period.closeTime ? `${period.openTime.hours}:${period.openTime.minutes?.toString().padStart(2, '0') || '00'} - ${period.closeTime.hours}:${period.closeTime.minutes?.toString().padStart(2, '0') || '00'}` : 'Closed'
        }));
      }
      return [];
    },
    formatDayName(day) {
      const daysMap = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      return daysMap[day?.toLowerCase()] || day;
    },
    formatWebsite(website) {
      if (!website) return '';
      return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
    },
    isCurrentDay(day) {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      return day?.toLowerCase() === today.toLowerCase();
    },
    getGoogleMapsSrc() {
      if (this.apifyLat && this.apifyLng) {
        return `https://www.google.com/maps?q=${this.apifyLat},${this.apifyLng}&output=embed`;
      }
      if (this.storeLocation) {
        const addrParts = [this.storeLocation.name || this.storeLocation.storeLocation, this.getFullAddress(this.storeLocation)].filter(Boolean);
        const addr = addrParts.join(', ');
        return `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
      }
      return '';
    },
    async fetchReviewsForLocation(location) {
      this.isReviewsLoading = true;
      this.reviews = [];
      // Reset pagination when fetching new reviews
      this.reviewsPage = 1;
      try {
        // Prepare account info for the selected location
        const currentUID = this.userUid;
        const wid = this.locationId;
        // Get workspace details
        const workspaceRef = doc(db, "users", currentUID, "fly_workspaces", wid);
        const workspaceDoc = await getDoc(workspaceRef);
        let isShallow = false;
        let placeId = workspaceDoc.data()?.placeId;
        if (workspaceDoc.exists() && workspaceDoc.data()?.shallowConnect) {
          isShallow = true;
        }
        let accounts = [];
        if (isShallow) {
          accounts.push({
            uid: currentUID,
            wid,
            location: placeId,
            isShallow: true
          });
        } else {
          // For regular workspaces, check for GMB connection
          const connectedAppsRef = collection(db, "users", currentUID, "fly_workspaces", wid, "connected-apps");
          const gbpSnapshot = await getDocs(query(connectedAppsRef, where("__name__", "==", "google_business_profile")));
          if (!gbpSnapshot.empty) {
            const gbpData = gbpSnapshot.docs[0].data();
            const accountId = gbpData.accounts?.[0]?.id || "";
            (gbpData.accounts || []).forEach((account) => {
              const locationId = account.locations?.[0]?.name || "";
              accounts.push({
                uid: currentUID,
                wid,
                location: locationId,
                account: accountId,
                isShallow: false
              });
            });
          }
        }
        if (accounts.length === 0) {
          this.reviews = [];
          return;
        }
        const payload = {
          uid: currentUID,
          accounts,
          action: "get_gmb_reviews",
          platform: "google_business_profile",
          page: 1,
          pageSize: 20, // Get more reviews to filter for positive ones
        };
        console.log('[fetchReviewsForLocation] Payload:', payload);
        let idToken = null;
        if (auth.currentUser) {
          idToken = await auth.currentUser.getIdToken();
        }
        const headers = {
          "Content-Type": "application/json",
        };
        if (idToken) {
          headers["Authorization"] = `Bearer ${idToken}`;
        }
        console.log('[fetchReviewsForLocation] ID Token:', idToken);
        console.log('[fetchReviewsForLocation] Endpoint:', `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`);
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ data: payload }),
          }
        );
        console.log('[fetchReviewsForLocation] Response:', response);
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const reviews = result?.data?.reviews || [];
            const ratingMap = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
            const now = new Date();
            const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            this.reviews = reviews
              .map((review) => {
                return {
                  reviewId: review.reviewId,
                  displayName: review.reviewer?.displayName || "Anonymous",
                  profilePhotoUrl: review.reviewer?.profilePhotoUrl || 'https://www.gravatar.com/avatar/?d=mp',
                  starRating: ratingMap[review.starRating] || 0,
                  comment: review.comment || "",
                  updateTime: review.updateTime,
                };
              })
              .filter((review) => {
                // Only include positive reviews (4+ stars) with text content
                if (!review.updateTime || !review.comment || review.comment.trim().length === 0) return false;
                if (review.starRating < 4) return false; // Only 4+ star reviews
                const reviewDate = new Date(review.updateTime);
                return reviewDate >= oneMonthAgo && reviewDate <= now;
              })
              .slice(0, 15); // Keep top 15 positive reviews
          }
        }
        console.log('[fetchReviewsForLocation] Positive reviews:', this.reviews);
      } catch (e) {
        console.error('[fetchReviewsForLocation] Error:', e);
        this.reviews = [];
      } finally {
        this.isReviewsLoading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    // Add pagination methods for reviews
    nextReviewPage() {
      if (this.hasNextReviewPage) {
        this.reviewsPage++;
      }
    },
    prevReviewPage() {
      if (this.hasPrevReviewPage) {
        this.reviewsPage--;
      }
    },
    updateFavicon() {
      // Skip favicon update if disabled
      if (this.disableTitleAndFavicon) {
        console.log("🚫 [BRAND LOGO] Favicon update disabled for preview mode");
        return;
      }
      
      // Only update favicon if we have a valid logo URL (not the default SVG)
      const logoUrl = this.storeLogo;
      const defaultSvg = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgMThjLTQuNDEgMC04LTMuNTktOC04cy41OS04IDggOCA4IDMuNTkgOCA4LTMuNTkgOHoiIGZpbGw9IiNlNWU3ZWIiLz48L3N2Zz4=';
      
      if (!logoUrl || logoUrl === defaultSvg) {
        console.log("🚫 [BRAND LOGO] No valid logo URL available for favicon");
        return;
      }

      console.log("🔄 [BRAND LOGO] Updating favicon with logo:", logoUrl);

      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll("link[rel~='icon']");
      existingFavicons.forEach(link => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
        }
      });

      // Create new favicon link
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = logoUrl;
      link.type = 'image/x-icon';
      document.head.appendChild(link);

      console.log("✅ [BRAND LOGO] Favicon updated successfully with URL:", logoUrl);
    },

    updatePageTitle() {
      // Skip title update if disabled
      if (this.disableTitleAndFavicon) {
        console.log("🚫 [BRAND LOGO] Page title update disabled for preview mode");
        return;
      }
      
      if (this.brandName && this.brandName !== 'Store Brand') {
        let newTitle = `${this.brandName}`;
        
        // Add location name if available
        if (this.storeLocation && (this.storeLocation.name || this.storeLocation.storeLocation)) {
          const locationName = this.storeLocation.name || this.storeLocation.storeLocation;
          newTitle += ` - ${locationName}`;
        }
        
        console.log("Updating page title to:", newTitle);
        document.title = newTitle;
      }
    },
    scrollToAppointment() {
      const appointmentSection = document.getElementById('appointment');
      if (appointmentSection) {
        appointmentSection.scrollIntoView({ behavior: 'smooth' });
      }
    },
    async submitAppointment() {
      this.isSubmitting = true;
      try {
        // Handle form submission
        console.log('Appointment form submitted:', this.appointmentForm);
        // Reset form
        this.appointmentForm = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          service: '',
          message: ''
        };
        // Show success message
        console.log('Appointment booked successfully!');
      } catch (e) {
        console.error('Error submitting appointment:', e);
      } finally {
        this.isSubmitting = false;
      }
    },
    callBusiness() {
      const phone = this.getGmbPhone(this.storeLocation);
      if (phone) {
        const url = `tel:${phone}`;
        window.open(url, '_system');
      }
    },
    handleTabClick(tab) {
      console.log('Tab clicked:', tab);
      console.log('Current route:', this.$route);
      console.log('Current props:', { userUid: this.userUid, wid: this.wid, locationId: this.locationId, slug: this.slug });
      
      this.activeTab = tab.value;
      
      // Navigate to the appropriate route
      if (tab.value === 'home') {
        // Navigate to the base route without tabvalue
        const routeParams = {
          name: 'individual-store',
          params: {
            userUid: this.userUid,
            wid: this.wid,
            locationId: this.locationId
          }
        };
        
        // Add slug only if it exists
        if (this.slug) {
          routeParams.params.slug = this.slug;
        }
        
        console.log('Navigating to home:', routeParams);
        this.$router.push(routeParams);
      } else {
        // Navigate to the tab-specific route
        const routeParams = {
          name: 'individual-store-tab',
          params: {
            userUid: this.userUid,
            wid: this.wid,
            locationId: this.locationId,
            tabvalue: tab.value
          }
        };
        
        // Add slug only if it exists
        if (this.slug) {
          routeParams.params.slug = this.slug;
        }
        
        console.log('Navigating to tab:', routeParams);
        this.$router.push(routeParams);
      }
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    visitWebsite() {
      if (this.storeLocation && this.storeLocation.website) {
        window.open(this.storeLocation.website, '_blank');
      }
    },
    async fetchGalleryPosts() {
      this.isGalleryLoading = true;
      this.galleryPosts = [];
      
      try {
        if (!this.storeLocation || !this.locationId) {
          console.log('[fetchGalleryPosts] No location or location ID provided');
        return;
      }

        console.log('[fetchGalleryPosts] Fetching posts for location:', this.locationId);
        console.log('[fetchGalleryPosts] Using workspace ID:', this.wid);
        
        // Calculate date range for last month
        const now = new Date();
        const oneMonthAgo = new Date(new Date().setDate(new Date().getDate() - 30)); // 30 days ago
        
        console.log('[fetchGalleryPosts] Date range - Start:', oneMonthAgo, 'End:', now);
        
        // Use the same approach as StoreLocator - use location.id as wid
        const fetchSocialPosts = httpsCallable(functions, "fetchSocialPosts");
        
        const payload = {
          uid: this.userUid,
          wid: this.locationId, // Use location ID as wid like StoreLocator
          operation: "fetchDateRangePosts",
          startDate: oneMonthAgo,
          endDate: now,
          limit: 6
        };
        
        console.log('[fetchGalleryPosts] Payload:', payload);
        
        const response = await fetchSocialPosts(payload);
        console.log('[fetchGalleryPosts] Response:', response);
        
        if (response.data && response.data.result) {
          const fetchedPosts = response.data.result;
          
          // Get platform data for social media icons (same as StoreLocator)
          const platformsRef = collection(db, "fly_platforms");
          const platformsSnapshot = await getDocs(platformsRef);
          const platforms = {};
          platformsSnapshot.forEach(doc => {
            platforms[doc.id] = doc.data();
          });

          // Process posts to add platform information (same as StoreLocator)
          this.galleryPosts = fetchedPosts.map((post, index) => {
            const socialMediaArr = [];
            if (post.social_media && Array.isArray(post.social_media)) {
              post.social_media.forEach(media => {
                const platformName = typeof media === 'string' ? media : media.name;
                const platformData = platforms[platformName];
                if (platformData) {
                  socialMediaArr.push({
                    name: platformName,
                    label: platformData.name,
                    logo_url: platformData.logo_url,
                  });
                }
              });
            }
            return {
              ...post,
              id: post.id || `post_${index}_${Date.now()}`, // Ensure unique ID
              social_media: socialMediaArr
            };
          });

          console.log('[fetchGalleryPosts] Successfully fetched posts:', this.galleryPosts.length);
        } else {
          console.log('[fetchGalleryPosts] No posts data in response');
          this.galleryPosts = [];
        }
      } catch (error) {
        console.error('[fetchGalleryPosts] Error fetching posts:', error);
        // Fallback: try to get posts from location data if available (same as StoreLocator)
        if (this.storeLocation?.recent_posts && Array.isArray(this.storeLocation.recent_posts)) {
          console.log('[fetchGalleryPosts] Using fallback posts from location data');
          this.galleryPosts = this.storeLocation.recent_posts.map((post, index) => ({
            ...post,
            id: post.id || `fallback_post_${index}_${Date.now()}`, // Ensure unique ID
          }));
        } else {
          this.galleryPosts = [];
        }
      } finally {
        this.isGalleryLoading = false;
      }
    },
    formatNumber(num) {
      if (!num) return '0';
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    },
    formatDate(date) {
      if (!date) return '';
      const dateObj = date.toDate?.() || new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - dateObj);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Today';
      if (diffDays === 2) return 'Yesterday';
      if (diffDays <= 7) return `${diffDays - 1} days ago`;
      
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    },
    toggleReadMore(postId) {
      console.log('[toggleReadMore] Toggling post ID:', postId);
      console.log('[toggleReadMore] Current expandedPosts state:', this.expandedPosts);
      console.log('[toggleReadMore] Current state for this post:', this.expandedPosts[postId]);
      
      // Create a new object to ensure reactivity
      const newExpandedPosts = { ...this.expandedPosts };
      newExpandedPosts[postId] = !newExpandedPosts[postId];
      this.expandedPosts = newExpandedPosts;
      
      console.log('[toggleReadMore] New state for this post:', this.expandedPosts[postId]);
      console.log('[toggleReadMore] Updated expandedPosts state:', this.expandedPosts);
    },
    getSocialPlatformConfig(platform) {
      const platformConfigs = {
        facebook: { 
          name: 'Facebook', 
          icon: 'mdi-facebook', 
          iconColor: '#1877F2' 
        },
        instagram: { 
          name: 'Instagram', 
          icon: 'mdi-instagram', 
          iconColor: '#E4405F' 
        },
        twitter: { 
          name: 'Twitter', 
          icon: 'mdi-twitter', 
          iconColor: '#1DA1F2' 
        },
        youtube: { 
          name: 'YouTube', 
          icon: 'mdi-youtube', 
          iconColor: '#FF0000' 
        },
        threads: { 
          name: 'Threads', 
          icon: 'mdi-message-text', 
          iconColor: '#000000' 
        },
        linkedin: { 
          name: 'LinkedIn', 
          icon: 'mdi-linkedin', 
          iconColor: '#0A66C2' 
        },
        tiktok: { 
          name: 'TikTok', 
          icon: 'mdi-music-note', 
          iconColor: '#000000' 
        },
        whatsapp: { 
          name: 'WhatsApp', 
          icon: 'mdi-whatsapp', 
          iconColor: '#25D366' 
        }
      };
      
      // Handle both lowercase and original case
      const normalizedPlatform = platform.toLowerCase();
      return platformConfigs[normalizedPlatform] || null;
    },
    getGmbPhone(location) {
      if (location?.gmbProfile?.phoneNumbers?.primaryPhone) return location.gmbProfile.phoneNumbers.primaryPhone;
      if (location?.gmbProfile?.phoneNumbers?.secondaryPhone) return location.gmbProfile.phoneNumbers.secondaryPhone;
      return null;
    },
    async fetchGMBSettingsData() {
      try {
        console.log('[fetchGMBSettingsData] Starting to fetch GMB settings...');
        
        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.userUid,
          "fly_workspaces",
          this.wid,
          "gmb-settings"
        );
        const latestDocQuery = query(
          gmbSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const gmbData = latestDoc.data().gmb_details;
          console.log('[fetchGMBSettingsData] GMB data found:', gmbData);
          
          this.gmbSettingsData = gmbData;
          
          if (gmbData.productsAndServices) {
            console.log('[fetchGMBSettingsData] Products and services found:', gmbData.productsAndServices);
          } else {
            console.log('[fetchGMBSettingsData] No products and services found in GMB data');
          }
        } else {
          console.log('[fetchGMBSettingsData] No GMB settings documents found');
          this.gmbSettingsData = null;
        }
      } catch (error) {
        console.error('[fetchGMBSettingsData] Error fetching GMB settings:', error);
        this.gmbSettingsData = null;
      }
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
  mounted() {
    // Store original favicon reference
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      this._originalFaviconHref = favicon.href;
    }

    // Store original page title
    this._originalPageTitle = document.title;

    // Set initial active tab from prop or default to home
    this.activeTab = this.tabvalue || 'home';

    this.fetchStoreData();
    // this.fetchGalleryPosts(); // No longer needed here
  },
  beforeDestroy() {
    // Only restore if title and favicon updates were not disabled
    if (!this.disableTitleAndFavicon) {
      // Restore original favicon
      if (this._originalFaviconHref) {
        const favicon = document.querySelector("link[rel~='icon']");
        if (favicon) {
          favicon.href = this._originalFaviconHref;
          console.log("Restored original favicon:", this._originalFaviconHref);
        }
      }
      
      // Clean up any custom favicon links we created
      const customFavicons = document.querySelectorAll("link[rel~='icon']");
      customFavicons.forEach(link => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
          console.log("Removed custom favicon link");
        }
      });

      // Restore original page title
      if (this._originalPageTitle) {
        console.log("Restoring original page title:", this._originalPageTitle);
        document.title = this._originalPageTitle;
      }
    }
  },
};
</script>

<style scoped>
/* Clean, Professional Design inspired by droswalsdental.com */

/* Base Styles */
.min-h-screen {
  background: var(--store-background);
  min-height: 100vh;
}

/* Header Styles */
header {
  position: relative !important;
  background: var(--store-background) !important;
  border-bottom: 1px solid var(--store-border-color) !important;
  box-shadow: 0 2px 10px var(--store-shadow) !important;
}

/* Hero Section */
.bg-gradient-to-br {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%) !important;
}

/* Card Styles */
.bg-white {
  background: var(--store-background) !important;
  border: 1px solid var(--store-border-color) !important;
  box-shadow: 0 4px 6px -1px var(--store-shadow), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

/* Button Styles */
button {
  font-weight: 600 !important;
}

/* Form Styles */
input, select, textarea {
  border: 1px solid var(--store-border-color) !important;
  border-radius: 0.5rem !important;
  padding: 0.75rem 1rem !important;
  font-size: 1rem !important;
}

input:focus, select:focus, textarea:focus {
  outline: none !important;
  border-color: var(--store-primary) !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

/* Gradient Buttons */
.bg-gradient-to-r {
  background: linear-gradient(to right, var(--store-gradient-start), var(--store-gradient-end)) !important;
}

/* Section Spacing */
section {
  padding: 4rem 1.5rem !important;
}

/* Typography */
h1, h2, h3, h4 {
  font-weight: 700 !important;
  color: var(--store-text-primary) !important;
}

p {
  color: var(--store-text-secondary) !important;
  line-height: 1.6 !important;
}

/* Icon Styles */
.v-icon {
  filter: none !important;
}

/* Footer */
footer {
  background: #111827 !important;
  color: #ffffff !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  section {
    padding: 2rem 1rem !important;
  }
  
  h1 {
    font-size: 2.5rem !important;
  }
  
  h2 {
    font-size: 2rem !important;
  }
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

/* Loading States */
.disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Review Styles */
.review-item {
  background: var(--store-background) !important;
  border: 1px solid var(--store-border-color) !important;
  border-radius: 1rem !important;
  padding: 1.5rem !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.review-author {
  font-weight: 600 !important;
  color: var(--store-text-primary) !important;
  font-size: 0.95rem !important;
}

.review-date {
  font-size: 0.8rem !important;
  color: var(--store-text-secondary) !important;
}

.review-comment {
  color: var(--store-text-primary) !important;
  font-size: 0.95rem !important;
  line-height: 1.6 !important;
  font-style: italic !important;
}

/* Pagination Styles */
.pagination-container {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-top: 2rem !important;
  padding-top: 1.5rem !important;
  border-top: 1px solid var(--store-border-color) !important;
  flex-wrap: wrap !important;
  gap: 1rem !important;
}

.pagination-info {
  font-size: 0.9rem !important;
  color: var(--store-text-secondary) !important;
  font-weight: 500 !important;
}

.pagination-page-info {
  font-size: 0.9rem !important;
  color: var(--store-text-secondary) !important;
  font-weight: 500 !important;
  padding: 0 1rem !important;
  background: #f3f4f6 !important;
  border-radius: 0.5rem !important;
  min-width: 80px !important;
  text-align: center !important;
}

/* Star Rating */
.star-icon {
  filter: none !important;
}

/* Business Hours Highlight */
.bg-blue-50 {
  background-color: #eff6ff !important;
}

.border-blue-200 {
  border-color: #bfdbfe !important;
}

/* Contact Info Icons */
.w-12.h-12 {
  width: 3rem !important;
  height: 3rem !important;
}

/* Service Cards */
.w-16.h-16 {
  width: 4rem !important;
  height: 4rem !important;
}

/* Appointment Form */
.form-group {
  margin-bottom: 1.5rem !important;
}

/* Success/Error States */
.success {
  background: #dcfce7 !important;
  color: #166534 !important;
  border: 1px solid #bbf7d0 !important;
}

.error {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus States */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid var(--store-primary) !important;
  outline-offset: 2px !important;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }
}

/* Appointment Button Styles */
.appointment-button {
  background: var(--appointment-button-bg-color) !important;
  color: var(--appointment-button-text-color) !important;
  border-radius: var(--appointment-button-border-radius) !important;
  border: var(--appointment-button-border-size) solid var(--appointment-button-border-color) !important;
  transition: all 0.2s ease-in-out !important;
}

.appointment-button:hover:not(:disabled) {
  background: var(--appointment-button-hover-bg-color) !important;
  color: var(--appointment-button-hover-text-color) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.appointment-button:active:not(:disabled) {
  transform: translateY(0) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

/* Gallery Styles */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gallery-card {
  transition: all 0.3s ease-in-out;
}

.gallery-card:hover {
  transform: translateY(-4px);
}

.gallery-image {
  transition: transform 0.3s ease-in-out;
}

.gallery-image:hover {
  transform: scale(1.05);
}

/* Platform Badge Colors */
.platform-badge {
  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.7);
}
</style> 